import './index.scss';

import { Component, ReactNode } from 'react';

import TabsContext, { TabsContextValue } from '../BraxTabs/context';

export type BraxTabProps<T extends string = string> = {
    id: T,
    className?: string | boolean,
    title: ReactNode,
    disabled: boolean,
    prerender?: boolean,
    unmount?: boolean,
    hidden?: boolean,
    children: ReactNode
};

type BraxTabState = {
    mounted: boolean
};

export class BraxTab<T extends string = string> extends Component<BraxTabProps<T>, BraxTabState> {

    static defaultProps = {
        disabled: false
    };

    static contextType = TabsContext;

    declare context: TabsContextValue<T>;

    get prerender() {
        return this.props.prerender !== undefined ? this.props.prerender : this.context.prerender;
    }

    get unmount() {
        return this.props.unmount !== undefined ? this.props.unmount : this.context.unmount;
    }

    get hidden() {
        return this.props.hidden !== undefined ? this.props.hidden : this.context.hidden;
    }

    get className() {
        return this.props.className === false || (this.props.className === undefined && !this.context.className)
            ? ''
        : typeof this.props.className === 'string'
            ? this.props.className
            : `${this.props.id}-tab`;
    }

    state = {
        mounted: this.prerender && !this.unmount || !this.props.disabled && this.context.active === this.props.id
    };

    componentDidMount() {
        this.context.add({ ...this.props });
    }

    componentDidUpdate(prevProps: BraxTabProps<T>) {
        if (prevProps.title !== this.props.title)
            this.context.setTitle(this.props.id, this.props.title);

        if (prevProps.disabled !== this.props.disabled) {
            this.context.setDisabled(this.props.id, this.props.disabled);

            if (!this.props.disabled)
                this.setState(prevState => !prevState.mounted ? { mounted: true } : null);
        }

        if (!this.unmount && !this.state.mounted && this.context.active === this.props.id)
            this.setState({ mounted: true });
    }

    componentWillUnmount() {
        this.context.remove(this.props.id);
    }

    render() {
        const show = this.context.active === this.props.id || this.context.animating === this.props.id;

        return (
            <div className={`brax-tab ${this.className} ${this.hidden && !show ? 'brax-tab-hidden' : ''}`}>
                <div>{ show || !this.unmount && this.state.mounted ? this.props.children : null }</div>
            </div>
        );
    }

}
